import {
  EventKind,
  IPathQueryFieldsFragment,
  ITopPathsSliceFieldsFragment,
  PathModeKey,
  QueryType,
} from '../apollo/graphqlSchema';
import {
  IParsedChartDataPath,
  IParsedChartDataPathSankey,
  IParsedChartDataPathTopPaths,
} from '../../components/Chart/interfaces';
import { parseAliasReplacements, toAliasableSlice } from './util';
import { assertNotNil, isNotNil } from '../types';
import produce from 'immer';
import { generateTransientAnalysisId } from '../../redux/utils/transientAnalysis';
import { MaybeNil } from '../interfaces';
import { isNil } from 'lodash';
import { Draft } from '@reduxjs/toolkit';
import { IStateHint } from '../stateHint';
import { IPathAnalysis, IPathAnalysisData } from '../apollo/responseTypes';
import { UnreachableCaseError } from 'ts-essentials';

const transformSankeyChartData = (
  sankeyData: IPathAnalysisData['modeValue'] & {
    __typename: 'SankeyPathModeValue';
  },
  analysis: IPathAnalysis,
): IParsedChartDataPathSankey => {
  const combinedFlows = [sankeyData.base, ...sankeyData.flows].filter(isNotNil);
  const aliasableFlows = combinedFlows.map(toAliasableSlice);
  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements,
  );

  return {
    queryType: QueryType.Path,
    mode: PathModeKey.Sankey,
    data: [],
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    slices: aliasableFlows, // flows are actually slices in Kobol data model
    maxSeq: 0,
    eventsPerStep: 4,
    directionKey: analysis.query.direction.key,
    canCreateFunnel: checkCanCreateFunnel(analysis.query),
  };
};

type IMaybePathBaseSlice = (IParsedChartDataPath & {
  mode: PathModeKey.TopPaths;
})['base'];

const updateTopPathFunnel = (
  slice: Draft<ITopPathsSliceFieldsFragment>,
  prefix: string,
) => {
  slice.topPaths.forEach((topPath, pathIndex) => {
    topPath.funnel.id = generateTransientAnalysisId(`${prefix}_${pathIndex}`);
  });
};

const updateBaseTopPath = (
  prefix: string,
  base: MaybeNil<ITopPathsSliceFieldsFragment>,
): IMaybePathBaseSlice => {
  if (isNil(base)) {
    return null;
  }
  const aliasableBaseSlice = toAliasableSlice(base);
  return produce(aliasableBaseSlice, (draft) => {
    updateTopPathFunnel(draft, `${prefix}_overall`);
  });
};

export const checkCanCreateFunnel = (
  pathQuery: IPathQueryFieldsFragment,
): IStateHint => {
  if (
    pathQuery.mergeEvents.length > 0 ||
    pathQuery.expandEvents.length > 0 ||
    pathQuery.foldEvents.length > 0
  ) {
    return {
      isDisabled: true,
      hint: "You can't create a funnel from a path which contains Expand, Merge or Fold events",
    };
  } else {
    const allConditions = [
      ...pathQuery.startConditions,
      ...pathQuery.endConditions,
    ];
    if (
      allConditions.some((condition) =>
        condition.events.some((ev) => ev.kind === EventKind.Virtual),
      )
    ) {
      return {
        isDisabled: true,
        hint: "You can't create a funnel from a path which has Virtual Events in start or end event",
      };
    }
  }

  return {
    isDisabled: false,
  };
};

const transformTopPathsChartData = (
  topPathsData: IPathAnalysisData['modeValue'] & {
    __typename: 'TopPathsPathModeValue';
  },
  analysis: IPathAnalysis,
): IParsedChartDataPathTopPaths => {
  const aliasableSlices = topPathsData.slices.map(toAliasableSlice);
  const base = updateBaseTopPath(`${analysis.id}`, topPathsData.base);
  const slices = produce(aliasableSlices, (draft) => {
    draft.forEach((topPathSlice, sliceIndex) => {
      updateTopPathFunnel(topPathSlice, `${analysis.id}_slice_${sliceIndex}`);
    });
  });
  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements,
  );

  const transientAnalysisIds = [
    ...(base?.topPaths?.map((topPath) => topPath.funnel.id) ?? []),
    ...topPathsData.slices.map((slice) =>
      slice.topPaths.map((topPath) => topPath.funnel.id),
    ),
  ].flat();

  return {
    queryType: QueryType.Path,
    mode: PathModeKey.TopPaths,
    data: [],
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    base,
    slices,
    canCreateFunnel: checkCanCreateFunnel(analysis.query),
    creator: analysis.creator,
    transientAnalysisIds,
  };
};

export const transformPathData = (
  analysis: IPathAnalysis,
): IParsedChartDataPath => {
  assertNotNil(analysis.data);
  const modeValue = analysis.data.modeValue;
  switch (modeValue.__typename) {
    case 'SankeyPathModeValue':
      return transformSankeyChartData(modeValue, analysis);
    case 'TopPathsPathModeValue':
      return transformTopPathsChartData(modeValue, analysis);
    default:
      throw new UnreachableCaseError(modeValue);
  }
};

export default transformPathData;
